// SNIGJ - Border Police Mobile Interface (PWA-ready with Offline Support)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, AlertTriangle, CheckCircle, User, 
  Scan, History, Clock,
  RefreshCw, MapPin, AlertOctagon, Wifi, WifiOff, Download, Cloud
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import offlineStorage from '../lib/offlineStorage';

const ALERT_COLORS = {
  CRITICAL: 'bg-red-600 animate-pulse',
  HIGH: 'bg-orange-500',
  MEDIUM: 'bg-yellow-500',
  CLEAR: 'bg-green-500'
};

const BorderPolicePage = () => {
  const { user, language, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('scan');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [stats, setStats] = useState({ today_scans: 0, today_alerts: 0, total_fugitives: 0, today_arrests: 0 });
  const [fugitives, setFugitives] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Offline state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncInfo, setSyncInfo] = useState({ lastSync: null, cachedFugitives: 0, pendingScans: 0 });
  const [syncing, setSyncing] = useState(false);
  
  // Scan form
  const [scanData, setScanData] = useState({
    national_id: '',
    passport_number: '',
    full_name: '',
    date_of_birth: '',
    checkpoint: user?.jurisdiction || 'CHECKPOINT_1'
  });

  useEffect(() => {
    // Initialize offline storage
    initOfflineStorage();
    
    // Online/offline listeners
    const handleOnline = () => {
      setIsOnline(true);
      toast.success(language === 'fr' ? 'Connexion rétablie' : 'Back online');
      syncPendingData();
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning(language === 'fr' ? 'Mode hors-ligne activé' : 'Offline mode activated');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Load data
    loadStats();
    loadHistory();
    loadFugitives();
    
    // Register service worker
    registerServiceWorker();
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered');
      } catch (error) {
        console.log('Service Worker registration failed:', error);
      }
    }
  };

  const initOfflineStorage = async () => {
    try {
      await offlineStorage.init();
      const info = await offlineStorage.getLastSyncInfo();
      setSyncInfo(info);
    } catch (error) {
      console.error('Failed to init offline storage:', error);
    }
  };

  const syncPendingData = async () => {
    setSyncing(true);
    try {
      const result = await offlineStorage.syncOfflineScans();
      if (result.synced > 0) {
        toast.success(`${result.synced} scan(s) synchronisé(s)`);
      }
      loadStats();
      loadHistory();
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
      const info = await offlineStorage.getLastSyncInfo();
      setSyncInfo(info);
    }
  };

  const downloadFugitivesForOffline = async () => {
    setSyncing(true);
    try {
      const response = await api.get('/border/fugitives?limit=500');
      const count = await offlineStorage.cacheFugitives(response.data.fugitives || []);
      toast.success(`${count} fugitifs téléchargés pour utilisation hors-ligne`);
      const info = await offlineStorage.getLastSyncInfo();
      setSyncInfo(info);
    } catch (error) {
      toast.error('Erreur de téléchargement');
    } finally {
      setSyncing(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/border/statistics');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      // Use cached data if offline
      if (!navigator.onLine) {
        const info = await offlineStorage.getLastSyncInfo();
        setStats(prev => ({ ...prev, total_fugitives: info.cachedFugitives }));
      }
    }
  };

  const loadHistory = async () => {
    try {
      const response = await api.get('/border/scan-history?limit=20');
      setScanHistory(response.data.scans || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const loadFugitives = async () => {
    try {
      if (navigator.onLine) {
        const response = await api.get('/border/fugitives');
        setFugitives(response.data.fugitives || []);
      } else {
        // Load from offline cache
        const cached = await offlineStorage.getCachedFugitives();
        setFugitives(cached);
      }
    } catch (error) {
      console.error('Failed to load fugitives:', error);
      // Fallback to cached data
      const cached = await offlineStorage.getCachedFugitives();
      setFugitives(cached);
    }
  };

  const handleScan = async () => {
    if (!scanData.national_id && !scanData.passport_number && !scanData.full_name) {
      toast.error(language === 'fr' ? 'Entrez au moins un identifiant' : 'Enter at least one identifier');
      return;
    }

    setScanning(true);
    setScanResult(null);

    // OFFLINE MODE - Check against cached fugitives
    if (!navigator.onLine) {
      try {
        const offlineResult = await offlineStorage.checkFugitiveOffline({
          national_id: scanData.national_id,
          passport_number: scanData.passport_number,
          full_name: scanData.full_name
        });

        const result = {
          scan_id: `offline_${Date.now()}`,
          alert_level: offlineResult.match ? 'CRITICAL' : 'CLEAR',
          alert_message: offlineResult.match 
            ? `FUGITIF RECHERCHÉ (CACHE): ${offlineResult.fugitive.reason || 'Mandat d\'arrêt'}`
            : 'Aucune correspondance dans le cache local',
          match_found: offlineResult.match,
          matched_record: offlineResult.fugitive,
          scanned_at: new Date().toISOString(),
          offline: true,
          instructions: offlineResult.match 
            ? 'ARRÊTEZ IMMÉDIATEMENT L\'INDIVIDU. Vérifiez en ligne dès que possible.'
            : 'Vérification hors-ligne uniquement. Confirmez en ligne si possible.'
        };

        setScanResult(result);

        // Save to offline queue
        await offlineStorage.saveOfflineScan({
          ...scanData,
          result: result.alert_level,
          match_found: result.match_found
        });

        if (offlineResult.match) {
          if (navigator.vibrate) navigator.vibrate([500, 200, 500, 200, 500]);
        }

        const info = await offlineStorage.getLastSyncInfo();
        setSyncInfo(info);
      } catch (error) {
        toast.error('Erreur de scan hors-ligne');
      } finally {
        setScanning(false);
      }
      return;
    }

    // ONLINE MODE
    try {
      const response = await api.post('/border/scan', scanData);
      setScanResult(response.data);
      
      if (response.data.alert_level === 'CRITICAL') {
        // Play alert sound
        const audio = new Audio('/alert-sound.mp3');
        audio.play().catch(() => {});
        
        // Vibrate on mobile
        if (navigator.vibrate) {
          navigator.vibrate([500, 200, 500, 200, 500]);
        }
      }
      
      loadHistory();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan error');
    } finally {
      setScanning(false);
    }
  };

  const clearScan = () => {
    setScanResult(null);
    setScanData({ national_id: '', passport_number: '', full_name: '', date_of_birth: '', checkpoint: scanData.checkpoint });
  };

  const filteredFugitives = fugitives.filter(f => 
    !searchQuery || 
    f.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.national_id?.includes(searchQuery)
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="border-police-page">
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 bg-slate-800 border-b border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-slate-900" />
            </div>
            <div>
              <h1 className="font-bold text-lg">SNIGJ Police</h1>
              <div className="flex items-center gap-2">
                <p className="text-xs text-slate-400">{user?.name || 'Agent'}</p>
                {isOnline ? (
                  <Wifi className="w-3 h-3 text-green-400" />
                ) : (
                  <WifiOff className="w-3 h-3 text-orange-400" />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={downloadFugitivesForOffline}
              disabled={syncing || !isOnline}
              className="text-slate-400"
            >
              {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400">
              {language === 'fr' ? 'Déco' : 'Logout'}
            </Button>
          </div>
        </div>
        
        {/* Offline Status Bar */}
        {!isOnline && (
          <div className="mt-2 p-2 bg-orange-500/20 rounded text-center text-xs text-orange-300">
            <WifiOff className="w-3 h-3 inline mr-1" />
            MODE HORS-LIGNE - {syncInfo.cachedFugitives} fugitifs en cache
            {syncInfo.pendingScans > 0 && ` | ${syncInfo.pendingScans} scan(s) en attente`}
          </div>
        )}
        
        {/* Sync Status */}
        {isOnline && syncInfo.pendingScans > 0 && (
          <div className="mt-2 p-2 bg-blue-500/20 rounded flex items-center justify-between text-xs">
            <span className="text-blue-300">
              <Cloud className="w-3 h-3 inline mr-1" />
              {syncInfo.pendingScans} scan(s) à synchroniser
            </span>
            <Button size="sm" variant="ghost" onClick={syncPendingData} disabled={syncing}>
              {syncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Sync'}
            </Button>
          </div>
        )}
      </header>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2 p-4 bg-slate-800">
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-400">{stats.today_scans}</p>
          <p className="text-[10px] text-slate-400">{language === 'fr' ? 'Scans' : 'Scans'}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-orange-400">{stats.today_alerts}</p>
          <p className="text-[10px] text-slate-400">{language === 'fr' ? 'Alertes' : 'Alerts'}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-red-400">{stats.total_fugitives}</p>
          <p className="text-[10px] text-slate-400">{language === 'fr' ? 'Fugitifs' : 'Fugitives'}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-400">{stats.today_arrests}</p>
          <p className="text-[10px] text-slate-400">{language === 'fr' ? 'Arrêts' : 'Arrests'}</p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
        <TabsList className="w-full bg-slate-800 rounded-none border-b border-slate-700 p-1">
          <TabsTrigger value="scan" className="flex-1 gap-1 data-[state=active]:bg-yellow-500 data-[state=active]:text-slate-900">
            <Scan className="w-4 h-4" />
            {language === 'fr' ? 'Scanner' : 'Scan'}
          </TabsTrigger>
          <TabsTrigger value="fugitives" className="flex-1 gap-1 data-[state=active]:bg-yellow-500 data-[state=active]:text-slate-900">
            <AlertTriangle className="w-4 h-4" />
            {language === 'fr' ? 'Fugitifs' : 'Fugitives'}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex-1 gap-1 data-[state=active]:bg-yellow-500 data-[state=active]:text-slate-900">
            <History className="w-4 h-4" />
            {language === 'fr' ? 'Historique' : 'History'}
          </TabsTrigger>
        </TabsList>

        {/* Scan Tab */}
        <TabsContent value="scan" className="p-4 space-y-4">
          {/* Scan Result Alert */}
          {scanResult && scanResult.scan_id && (
            <div key={scanResult.scan_id} className={`p-4 rounded-lg ${ALERT_COLORS[scanResult.alert_level] || 'bg-gray-500'} text-white`}>
              <div className="flex items-center gap-3 mb-3">
                {scanResult.alert_level === 'CRITICAL' ? (
                  <AlertOctagon className="w-10 h-10" />
                ) : scanResult.alert_level === 'CLEAR' ? (
                  <CheckCircle className="w-10 h-10" />
                ) : (
                  <AlertTriangle className="w-10 h-10" />
                )}
                <div>
                  <h3 className="text-xl font-bold">
                    {scanResult.alert_level === 'CRITICAL' && '🚨 ALERTE CRITIQUE'}
                    {scanResult.alert_level === 'HIGH' && '⚠️ ALERTE HAUTE'}
                    {scanResult.alert_level === 'MEDIUM' && '⚡ ATTENTION'}
                    {scanResult.alert_level === 'CLEAR' && '✓ PASSAGE AUTORISÉ'}
                  </h3>
                  <p className="text-sm opacity-90">{scanResult.alert_message}</p>
                </div>
              </div>
              
              {scanResult.matched_record && (
                <div className="bg-black/20 rounded p-3 mt-3">
                  <p className="font-medium">{scanResult.matched_record.full_name}</p>
                  {scanResult.matched_record.reason && (
                    <p className="text-sm opacity-90">{scanResult.matched_record.reason}</p>
                  )}
                </div>
              )}
              
              <div className="mt-3 p-3 bg-black/30 rounded text-sm">
                <p className="font-bold">{language === 'fr' ? 'Instructions:' : 'Instructions:'}</p>
                <p>{scanResult.instructions}</p>
              </div>

              <Button 
                variant="secondary" 
                className="w-full mt-4" 
                onClick={clearScan}
              >
                {language === 'fr' ? 'Nouveau scan' : 'New Scan'}
              </Button>
            </div>
          )}

          {/* Scan Form */}
          {!scanResult && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">{language === 'fr' ? 'Numéro carte d\'identité' : 'National ID'}</Label>
                <Input 
                  value={scanData.national_id}
                  onChange={(e) => setScanData({...scanData, national_id: e.target.value})}
                  placeholder="Ex: 123456789"
                  className="bg-slate-800 border-slate-700 text-white text-lg h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{language === 'fr' ? 'Numéro de passeport' : 'Passport Number'}</Label>
                <Input 
                  value={scanData.passport_number}
                  onChange={(e) => setScanData({...scanData, passport_number: e.target.value})}
                  placeholder="Ex: AB1234567"
                  className="bg-slate-800 border-slate-700 text-white text-lg h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{language === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
                <Input 
                  value={scanData.full_name}
                  onChange={(e) => setScanData({...scanData, full_name: e.target.value})}
                  placeholder="Ex: Jean Dupont"
                  className="bg-slate-800 border-slate-700 text-white text-lg h-12"
                />
              </div>

              <Button 
                onClick={handleScan} 
                disabled={scanning}
                className="w-full h-16 text-lg bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-bold"
              >
                {scanning ? (
                  <>
                    <RefreshCw className="w-6 h-6 mr-2 animate-spin" />
                    {language === 'fr' ? 'Vérification...' : 'Checking...'}
                  </>
                ) : (
                  <>
                    <Scan className="w-6 h-6 mr-2" />
                    {language === 'fr' ? 'SCANNER' : 'SCAN'}
                  </>
                )}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Fugitives Tab */}
        <TabsContent value="fugitives" className="p-4 space-y-4">
          <Input 
            placeholder={language === 'fr' ? 'Rechercher un fugitif...' : 'Search fugitive...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white"
          />

          <div className="space-y-3">
            {filteredFugitives.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{language === 'fr' ? 'Aucun fugitif trouvé' : 'No fugitives found'}</p>
              </div>
            ) : (
              filteredFugitives.map((fugitive) => (
                <div key={fugitive.fugitive_id} className="bg-slate-800 rounded-lg p-4 border border-red-500/30">
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded bg-slate-700 flex items-center justify-center">
                      {fugitive.photo_url ? (
                        <img src={fugitive.photo_url} alt="" className="w-full h-full object-cover rounded" />
                      ) : (
                        <User className="w-8 h-8 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold">{fugitive.full_name}</h4>
                      <p className="text-xs text-slate-400">
                        ID: {fugitive.national_id || 'N/A'} | Passport: {fugitive.passport_number || 'N/A'}
                      </p>
                      <p className="text-sm text-red-400 mt-1">{fugitive.reason}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={
                          fugitive.danger_level === 'EXTREME' ? 'bg-red-600' :
                          fugitive.danger_level === 'HIGH' ? 'bg-orange-500' :
                          'bg-yellow-500'
                        }>
                          {fugitive.danger_level}
                        </Badge>
                        {fugitive.last_known_location && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {fugitive.last_known_location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="p-4 space-y-4">
          <Button variant="outline" size="sm" onClick={loadHistory} className="w-full border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Actualiser' : 'Refresh'}
          </Button>

          <div className="space-y-2">
            {scanHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{language === 'fr' ? 'Aucun historique' : 'No history'}</p>
              </div>
            ) : (
              scanHistory.map((scan) => (
                <div 
                  key={scan.scan_id} 
                  className={`p-3 rounded-lg border ${
                    scan.alert_level === 'CRITICAL' ? 'bg-red-900/20 border-red-500/50' :
                    scan.alert_level === 'HIGH' ? 'bg-orange-900/20 border-orange-500/50' :
                    scan.alert_level === 'MEDIUM' ? 'bg-yellow-900/20 border-yellow-500/50' :
                    'bg-green-900/20 border-green-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {scan.scanned_data?.full_name || scan.scanned_data?.national_id || 'N/A'}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(scan.scanned_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                      </p>
                    </div>
                    <Badge className={ALERT_COLORS[scan.alert_level]?.replace('animate-pulse', '')}>
                      {scan.alert_level}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Bottom Navigation for Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-slate-700 p-2 safe-area-pb">
        <div className="grid grid-cols-3 gap-1">
          <button 
            onClick={() => setActiveTab('scan')}
            className={`flex flex-col items-center p-2 rounded ${activeTab === 'scan' ? 'bg-yellow-500 text-slate-900' : 'text-slate-400'}`}
          >
            <Scan className="w-6 h-6" />
            <span className="text-xs mt-1">{language === 'fr' ? 'Scanner' : 'Scan'}</span>
          </button>
          <button 
            onClick={() => setActiveTab('fugitives')}
            className={`flex flex-col items-center p-2 rounded ${activeTab === 'fugitives' ? 'bg-yellow-500 text-slate-900' : 'text-slate-400'}`}
          >
            <AlertTriangle className="w-6 h-6" />
            <span className="text-xs mt-1">{language === 'fr' ? 'Fugitifs' : 'Fugitives'}</span>
          </button>
          <button 
            onClick={() => setActiveTab('history')}
            className={`flex flex-col items-center p-2 rounded ${activeTab === 'history' ? 'bg-yellow-500 text-slate-900' : 'text-slate-400'}`}
          >
            <History className="w-6 h-6" />
            <span className="text-xs mt-1">{language === 'fr' ? 'Historique' : 'History'}</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default BorderPolicePage;
